/**
 * Forced password change.
 *
 * Shown when the account still holds an admin-issued credential. The server
 * blocks everything else until it is replaced (AUTH-006), so this screen is
 * deliberately a dead end: there is no navigation away from it, because there
 * is nowhere else the account can go.
 *
 * Changing the password revokes every session including this one, so the user
 * is returned to sign-in afterwards rather than silently continuing.
 */

import { useState, type FormEvent } from 'react';

import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { Button } from '../components/ui';

const MIN_LENGTH = 12;

export function ChangePassword() {
  const { signOut } = useSession();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirmPassword, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Checked here purely so the user finds out before a round trip; the server
  // enforces the real policy regardless of what this form allows.
  const tooShort = newPassword.length > 0 && newPassword.length < MIN_LENGTH;
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (newPassword !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'The two passwords do not match.' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/v1/auth/password/change', { currentPassword, newPassword });
      setDone(true);
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.fieldErrors);
        setError(caught.problem?.detail ?? 'Could not change your password.');
      } else {
        setError('Could not reach the server. Check your connection and try again.');
      }
      setSubmitting(false);
    }
  }

  const inputStyle = {
    borderRadius: 'var(--radius-sm)',
    borderColor: 'var(--color-rule-strong)',
    background: 'var(--color-surface)',
    color: 'var(--color-ink)',
  } as const;

  if (done) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-10">
        <div className="animate-rise w-full max-w-sm text-center">
          <h1 className="text-lg font-semibold tracking-tight">Password changed</h1>
          <p className="mt-2 text-sm" style={{ color: 'var(--color-ink-muted)' }}>
            All sessions were signed out, including this one. Sign in again with your new password.
          </p>
          <div className="mt-5">
            <Button variant="primary" onClick={() => void signOut()}>
              Go to sign in
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="animate-rise w-full max-w-sm">
        <header className="mb-6">
          <h1 className="text-lg font-semibold tracking-tight">Set a new password</h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-ink-subtle)' }}>
            Your account uses a temporary password issued by an administrator. Choose your own
            before continuing.
          </p>
        </header>

        <form
          onSubmit={handleSubmit}
          noValidate
          className="border p-5"
          style={{
            background: 'var(--color-surface)',
            borderColor: 'var(--color-rule)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <div className="mb-3">
            <label htmlFor="current" className="mb-1 block text-sm font-medium">
              Current password
            </label>
            <input
              id="current"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrent(e.target.value)}
              className="interactive w-full border px-2.5 py-2 text-base"
              style={inputStyle}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="next" className="mb-1 block text-sm font-medium">
              New password
            </label>
            <input
              id="next"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNew(e.target.value)}
              aria-describedby="next-hint"
              className="interactive w-full border px-2.5 py-2 text-base"
              style={{
                ...inputStyle,
                borderColor: tooShort ? 'var(--color-danger)' : inputStyle.borderColor,
              }}
            />
            <p
              id="next-hint"
              className="mt-1 text-xs"
              style={{ color: tooShort ? 'var(--color-danger)' : 'var(--color-ink-subtle)' }}
            >
              At least {MIN_LENGTH} characters. Length matters more than symbols.
            </p>
            {fieldErrors['newPassword'] !== undefined && (
              <p className="mt-1 text-xs" style={{ color: 'var(--color-danger)' }}>
                {fieldErrors['newPassword']}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="confirm" className="mb-1 block text-sm font-medium">
              Confirm new password
            </label>
            <input
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirm(e.target.value)}
              className="interactive w-full border px-2.5 py-2 text-base"
              style={{
                ...inputStyle,
                borderColor: mismatch ? 'var(--color-danger)' : inputStyle.borderColor,
              }}
            />
            {(mismatch || fieldErrors['confirmPassword'] !== undefined) && (
              <p className="mt-1 text-xs" style={{ color: 'var(--color-danger)' }}>
                {fieldErrors['confirmPassword'] ?? 'The two passwords do not match.'}
              </p>
            )}
          </div>

          {error !== null && (
            <div
              role="alert"
              className="animate-rise mb-3 border px-2.5 py-2 text-sm"
              style={{
                borderRadius: 'var(--radius-sm)',
                background: 'var(--color-danger-subtle)',
                borderColor: 'var(--color-danger)',
                color: 'var(--color-danger)',
              }}
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            disabled={tooShort || mismatch || newPassword.length === 0}
            style={{ width: '100%' }}
          >
            {submitting ? 'Changing' : 'Change password'}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <Button variant="ghost" onClick={() => void signOut()}>
            Sign out instead
          </Button>
        </div>
      </div>
    </main>
  );
}
